import type { LocationSummary, PreviewAction } from "../../api/contracts";

export interface LocationFilters { q: string; state: string; owner: string; enabled: "" | "true" | "false"; paused: "" | "true" | "false" }
export const EMPTY_FILTERS: LocationFilters = { q: "", state: "", owner: "", enabled: "", paused: "" };

/** Admins load all ~300 locations once and filter here; owners have a handful and no filter bar. */
export function filterLocations(list: LocationSummary[], f: LocationFilters): LocationSummary[] {
  const q = f.q.trim().toLowerCase();
  return list.filter((l) =>
    (!q || l.name.toLowerCase().includes(q)) &&
    (!f.state || l.state === f.state) &&
    (!f.owner || l.ownerIdentifier === f.owner) &&
    (!f.enabled || String(l.enabled) === f.enabled) &&
    (!f.paused || String(l.paused) === f.paused));
}

export function distinct(list: LocationSummary[], key: "state" | "ownerIdentifier"): string[] {
  return [...new Set(list.map((l) => l[key]).filter((v): v is string => !!v))].sort();
}

export interface PreviewSummary { create: number; remove: number; resize: number; skip: number; staff: number; days: number }
export function summarizePreview(actions: PreviewAction[]): PreviewSummary {
  const s: PreviewSummary = { create: 0, remove: 0, resize: 0, skip: 0, staff: 0, days: 0 };
  // A release changes nothing on the calendar, and an adopt or a lock keeps the block's time (only its title
  // becomes DNB), so both count as left alone.
  for (const a of actions) s[a.type === "release" || a.type === "lock" || a.type === "adopt" ? "skip" : a.type]++;
  s.staff = new Set(actions.map((a) => a.staffId)).size;
  s.days = new Set(actions.map((a) => a.date)).size;
  return s;
}

export function ruleScopeLabel(scope: "global" | "owner" | "location"): string {
  return scope === "global" ? "Corporate default" : scope === "owner" ? "Owner rules" : "Location rules";
}

/** "12 on · 3 paused · 41 off" for a page header. Paused counts as paused, not on. */
export function describeCounts(list: LocationSummary[]): string {
  const on = list.filter((l) => l.enabled && !l.paused).length;
  const paused = list.filter((l) => l.enabled && l.paused).length;
  const off = list.filter((l) => !l.enabled).length;
  return `${on} on · ${paused} paused · ${off} off`;
}
